import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

const THEME_KEY = 'crm_theme'; // 'porcelain' | 'graphite' | 'system'
const ThemeContext = createContext(null);

function readSystemDark() {
    return !!(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
}

export function ThemeProvider({ children }) {
    const [pref, setPref] = useState(() => {
        try {
            return localStorage.getItem(THEME_KEY) || 'porcelain';
        } catch {
            return 'porcelain';
        }
    });
    // Track OS-level dark preference independently from the user's stored `pref`.
    // Same-value setState bailouts would otherwise swallow live OS-theme changes
    // when the user has selected "System".
    const [systemDark, setSystemDark] = useState(readSystemDark);

    const theme = pref === 'system' ? (systemDark ? 'graphite' : 'porcelain') : pref;

    useEffect(() => {
        const rootEl = document.documentElement;
        rootEl.setAttribute('data-theme', theme);
        // Tailwind darkMode: ["class"] — keep the shadcn `dark` class in sync
        rootEl.classList.toggle('dark', theme === 'graphite');
        try {
            localStorage.setItem(THEME_KEY, pref);
        } catch {}
    }, [pref, theme]);

    useEffect(() => {
        if (!window.matchMedia) return;
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = (e) => setSystemDark(e.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    const setTheme = useCallback((next) => setPref(next), []);
    const toggleTheme = useCallback(() => {
        setPref(prev => {
            const active = prev === 'system' ? (readSystemDark() ? 'graphite' : 'porcelain') : prev;
            return active === 'graphite' ? 'porcelain' : 'graphite';
        });
    }, []);

    const value = useMemo(
        () => ({ pref, theme, setTheme, toggleTheme }),
        [pref, theme, setTheme, toggleTheme]
    );
    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
    return ctx;
}
